import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import { Dayjs } from "dayjs";
import {
  getPalletSummaryBranches,
  getPalletSummaryRoutes,
  getPalletSummaryTrips,
} from "../../services";
import { ApiResponse } from "../../types/express-data.type";
import {
  PalletRouteSummaryParams,
  PalletSummaryDateRange,
  PalletTripSummaryParams,
} from "../../types/pallet-summary.type";
import {
  API_DATE_FORMAT,
  PALLET_SUMMARY_LEVEL,
  PalletSummaryLevel,
} from "./constants";

interface LevelResponse<T> {
  /** The answer to the current request; null while it is loading, failed or not asked. */
  data: T | null;
  isLoading: boolean;
  isFailed: boolean;
}

/**
 * One level's response, asked again whenever `params` changes and not asked at all while it is
 * null. Only the latest request may settle: an older, slower answer is dropped, and nothing is
 * returned until the CURRENT params are answered, so a drill-down never shows the previous
 * level's figures under the new heading. `params` must keep its identity between renders.
 *
 * A new `revision` asks the same params again; their answer stays on screen until the new one
 * replaces it, since it is still the answer to what the page shows.
 */
function useLevelResponse<P, T>(
  request: (params: P) => Promise<AxiosResponse<ApiResponse<T>>>,
  params: P | null,
  revision: number,
): LevelResponse<T> {
  const [answer, setAnswer] = useState<{ params: P; data: T | null }>();

  useEffect(() => {
    if (params === null) return undefined;
    let isLatest = true;
    request(params)
      .then((response) => {
        if (isLatest) setAnswer({ params, data: response.data.data });
      })
      .catch(() => {
        // The interceptor has already reported the failure; the page shows it failed rather
        // than keep the previous filter's figures on screen.
        if (isLatest) setAnswer({ params, data: null });
      });
    return () => {
      isLatest = false;
    };
  }, [request, params, revision]);

  const current =
    params !== null && answer?.params === params ? answer : undefined;
  return {
    data: current?.data ?? null,
    isLoading: params !== null && current === undefined,
    isFailed: current !== undefined && current.data === null,
  };
}

interface PalletSummaryRequest {
  level: PalletSummaryLevel;
  startDay: Dayjs;
  endDay: Dayjs;
  /** Sent by an all-branch reader only; the server pins everyone else to their own branch. */
  branchCode: string | null;
  routeUuid: string | null;
  /** False while the picked range is wider than the API serves: nothing is asked for then. */
  isRangeAllowed: boolean;
}

/**
 * The responses behind สรุปถาด, one per level. A branch's routes stay loaded under its route
 * level too — they are the route picker's options and the breadcrumb's branch name — so
 * drilling into a route and back up again asks for them once. `reload` asks every open level
 * again after the page changed something on the server (a trip's หมายเหตุ, which the routes
 * count as well).
 */
export function usePalletSummary({
  level,
  startDay,
  endDay,
  branchCode,
  routeUuid,
  isRangeAllowed,
}: PalletSummaryRequest) {
  const [revision, setRevision] = useState(0);
  const startDate = startDay.format(API_DATE_FORMAT);
  const endDate = endDay.format(API_DATE_FORMAT);
  const isOverviewOpen = isRangeAllowed && level === PALLET_SUMMARY_LEVEL.overview;
  const isBranchOpen = isRangeAllowed && level !== PALLET_SUMMARY_LEVEL.overview;
  const openRouteUuid =
    isRangeAllowed && level === PALLET_SUMMARY_LEVEL.route ? routeUuid : null;

  const branchesParams = useMemo<PalletSummaryDateRange | null>(
    () => (isOverviewOpen ? { start_date: startDate, end_date: endDate } : null),
    [isOverviewOpen, startDate, endDate],
  );
  const routesParams = useMemo<PalletRouteSummaryParams | null>(
    () =>
      isBranchOpen
        ? {
            start_date: startDate,
            end_date: endDate,
            branch_code: branchCode ?? undefined,
          }
        : null,
    [isBranchOpen, startDate, endDate, branchCode],
  );
  const tripsParams = useMemo<PalletTripSummaryParams | null>(
    () =>
      openRouteUuid
        ? { start_date: startDate, end_date: endDate, route_uuid: openRouteUuid }
        : null,
    [openRouteUuid, startDate, endDate],
  );

  const branches = useLevelResponse(getPalletSummaryBranches, branchesParams, revision);
  const routes = useLevelResponse(getPalletSummaryRoutes, routesParams, revision);
  const trips = useLevelResponse(getPalletSummaryTrips, tripsParams, revision);

  const reload = useCallback(() => setRevision((current) => current + 1), []);

  return { branches, routes, trips, reload };
}
